import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useRetentionPolicy, useUpdateRetentionPolicy } from '../../hooks/usePrivacy';
import { ApiError } from '../../api/client';

const SLIDER_MARKS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 180, label: '6mo' },
  { value: 365, label: '1yr' },
  { value: 730, label: '2yr' },
];

export default function DataRetentionPage() {
  const { data: policy, isLoading, error: fetchError } = useRetentionPolicy();
  const updateMutation = useUpdateRetentionPolicy();

  const [days, setDays] = useState(90);
  const [autoDelete, setAutoDelete] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync form with fetched policy
  useEffect(() => {
    if (policy) {
      setDays(policy.retainGuestDataDays);
      setAutoDelete(policy.autoDeleteEnabled);
    }
  }, [policy]);

  const handleSave = async () => {
    setError(null);
    setSaved(false);

    try {
      await updateMutation.mutateAsync({
        retainGuestDataDays: days,
        autoDeleteEnabled: autoDelete,
      });
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to save settings. Please try again.');
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load retention settings. Please try again later.
      </Alert>
    );
  }

  const hasChanges =
    policy &&
    (days !== policy.retainGuestDataDays ||
      autoDelete !== policy.autoDeleteEnabled);

  return (
    <Box>
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
      >
        Data Retention
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Control how long guest data is kept after your events end. This helps
        you stay compliant with privacy regulations like GDPR.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Retention period */}
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Keep guest data for
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            After this period following an event, guest data will be eligible for
            deletion.
          </Typography>

          <Box sx={{ px: { xs: 1, sm: 2 }, mb: 3 }}>
            <Slider
              value={days}
              onChange={(_e, val) => setDays(val as number)}
              min={7}
              max={730}
              step={1}
              marks={SLIDER_MARKS}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v} days`}
            />
          </Box>

          <TextField
            label="Days"
            type="number"
            value={days}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val) && val >= 7 && val <= 3650) {
                setDays(val);
              }
            }}
            inputProps={{ min: 7, max: 3650 }}
            size="small"
            sx={{ width: 120, mb: 4 }}
          />

          {/* Auto-delete toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Automatically delete expired data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                When enabled, guest data past the retention period will be
                automatically anonymised.
              </Typography>
            </Box>
            <Switch
              checked={autoDelete}
              onChange={(e) => setAutoDelete(e.target.checked)}
              inputProps={{ 'aria-label': 'Toggle auto-delete' }}
            />
          </Box>

          {/* Warning */}
          {autoDelete && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ mt: 2 }}
            >
              <Typography variant="body2" fontWeight={500}>
                Deleted data cannot be recovered.
              </Typography>
              <Typography variant="body2">
                Once guest data is automatically deleted, it is permanently
                anonymised and cannot be restored. Make sure you have exported
                any data you need before enabling this feature.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Status messages */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {saved && (
          <Alert severity="success" onClose={() => setSaved(false)}>
            Settings saved successfully.
          </Alert>
        )}
      </Stack>

      {/* Save button */}
      <Button
        variant="contained"
        size="large"
        startIcon={
          updateMutation.isPending ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <SaveIcon />
          )
        }
        onClick={handleSave}
        disabled={updateMutation.isPending || !hasChanges}
        sx={{ py: 1.5 }}
      >
        {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
      </Button>
    </Box>
  );
}
